import { createContext, useContext, type ReactNode } from 'react';

import type { ProjectPanelLayout } from '../components/projectPanelLayoutModel';

/**
 * The live 3-way panel layout (OR-6123 F3), provided by `ProjectPage` and
 * read by descendants that need to know whether the content pane currently
 * owns the whole page (`artifacts-only`) vs. shares it with the RA chat
 * pane (`split`/`ra-only`) — a *mode*, not something a descendant's own
 * DOM width measurement can reliably tell it, since a wide browser window
 * can make the split-view content pane measure just as wide as a true
 * full-page layout.
 *
 * TODO: cleanup with research_assistant_gen_3 ff
 */
const ProjectPanelLayoutContext = createContext<ProjectPanelLayout | null>(null);

interface ProjectPanelLayoutProviderProps {
  layout: ProjectPanelLayout;
  children: ReactNode;
}

export const ProjectPanelLayoutProvider = ({
  layout,
  children,
}: ProjectPanelLayoutProviderProps) => (
  <ProjectPanelLayoutContext.Provider value={layout}>{children}</ProjectPanelLayoutContext.Provider>
);

/**
 * Read the current 3-way panel layout. Returns `null` outside `ProjectPage`
 * (e.g. a component test rendering the artifacts page standalone) —
 * callers should treat that as "unknown, assume split" rather than
 * defaulting to `artifacts-only`.
 */
export const useProjectPanelLayoutContext = (): ProjectPanelLayout | null => {
  return useContext(ProjectPanelLayoutContext);
};
